import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { IconButton, InlineButton } from '@platform/ui'
import cn from 'classnames'
import { useFocusTrap } from '@/features/wa/focusTrap/useFocusTrap'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { createFiltersSelector, setFilters } from '@/store/slices/aggregatesSlice'
import { CourseFilterCollection } from './CourseFilterCollection'
import { CourseFilterPriority } from './CourseFilterPriority'
import { CourseFilterProgress } from './CourseFilterProgress'
import styles from './CourseFiltersDrawer.module.scss'
import { CourseFilterTerm } from './CourseFilterTerm'
import { FilterDrawerType, FilterType } from './types'

type Props = {
  type: FilterDrawerType
  isOpen: boolean
  onClose: () => void
}

export const CourseFiltersDrawer = ({ type, isOpen, onClose }: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const filters = useAppSelector((state) => createFiltersSelector(state, type))

  const syncUrlFilters = useCallback(
    (key: string, value: string[]) => {
      const searchParams = new URLSearchParams(location.search)

      if (value.length > 0) {
        searchParams.set(key, value.join(','))
      } else {
        searchParams.delete(key)
      }

      navigate({ search: searchParams.toString() }, { replace: true })
    },
    [location.search, navigate],
  )

  const handleCheckboxChange = useCallback(
    (filterType: FilterType, value: string) => {
      const updatedFilter = (filters[filterType] as string[]).includes(value)
        ? filters[filterType].filter((item) => item !== value)
        : [...filters[filterType], value]

      dispatch(setFilters({ type, filters: { ...filters, [filterType]: updatedFilter } }))

      syncUrlFilters(filterType, updatedFilter)
    },
    [dispatch, filters, syncUrlFilters, type],
  )

  const handleClearFilters = useCallback(() => {
    dispatch(
      setFilters({
        type,
        filters: { courseProgress: [], coursePriority: [], term: [], collection: [] },
      }),
    )

    const searchParams = new URLSearchParams(location.search)

    searchParams.forEach((_, key) => {
      searchParams.delete(key)
    })
    navigate({ search: '' }, { replace: true })
  }, [dispatch, location.search, navigate, type])

  const shouldDisableClear = useMemo(() => {
    return Object.values(filters).every((filter) => !filter.length)
  }, [filters])

  const tabIndex = isOpen ? 0 : -1

  const ref = useFocusTrap(isOpen)

  return (
    <div className={cn(styles.drawer, { [styles.open]: isOpen })} tabIndex={tabIndex} ref={ref}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>{t('coursesFilterDrawer.filters')}</div>
        <IconButton
          shape="rounded"
          aria-label="Close drawer"
          iconName="CloseBoldStroke"
          color="tertiary"
          onClick={onClose}
          testId="close-drawer"
        />
      </div>

      <div className={styles.container}>
        <InlineButton
          iconBeforeText="FilterRemoveBoldStroke"
          text={t('coursesFilterDrawer.clearFilters')}
          onClick={handleClearFilters}
          color="neutral"
          size="small"
          disabled={shouldDisableClear}
          testId="clear-filters"
        />
      </div>

      {type === 'enrolled' && (
        <>
          <CourseFilterProgress type={type} onChange={handleCheckboxChange} />
          <CourseFilterPriority type={type} onChange={handleCheckboxChange} />
        </>
      )}
      {['enrolled', 'catalog'].includes(type) && <CourseFilterTerm type={type} onChange={handleCheckboxChange} />}
      <CourseFilterCollection type={type} onChange={handleCheckboxChange} />
    </div>
  )
}
